/**
 * ResultColumn — the answer.
 *
 * Single-column Kimi-style prose:
 *   - quiet status line (lane · cost · tools)
 *   - copy / .md actions
 *   - the answer body via Streamdown (same engine as dev-agent)
 *   - "Sources" strip at the bottom (numbered chips of cited artifacts)
 *   - collapsed "All evidence" details for the rest
 *
 * Inline [N] chips come from a markdown plugin that intercepts
 * `[label](artifact://<id>)` links.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Cloud, Copy, Download, FileText, FileType, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useShallow } from "zustand/react/shallow";
import { useCopyToClipboard, useUserIntent } from "@/hooks/multi-agent";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { linkifyEntities } from "@/components/molecules/multi-agent/ProseEntityLinker";
import { humanizeStatus, isWhitespace, triggerRagIngest } from "@/services/multi-agent";
import { exportArtifactMarkdown, sanitizeArtifactLabel, sanitizeArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";
import { downloadMarkdownAs, collectRenderedDiagrams, writePrintPlaceholder, type ReportDiagram } from "@/services/multi-agent/exportArtifact";
import { buildExportMarkdown } from "@/services/multi-agent/exportAppendix";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { fixMermaidUnquotedLabels } from "@/lib/studio/mermaidScrub";
import { ArtifactCard } from "@/components/molecules/multi-agent/ArtifactCard";
import { useArtifactOverlayStore } from "@/stores/multiAgentArtifactOverlayStore";
import { NoEvidenceBanner, ContractViolationBanner, AutoFixFailedBanner } from "@/components/molecules/multi-agent/RunBanners";
import { Narration } from "@/components/molecules/multi-agent/Narration";
import { WeaveShimmerCaption } from "@/components/molecules/multi-agent/WeaveShimmerCaption";
import { FollowUpChips } from "@/components/molecules/multi-agent/FollowUpChips";
import { CitationChip } from "@/components/molecules/multi-agent/CitationChip";
import { ReportExportBar } from "@/components/molecules/multi-agent/ReportExportBar";
import { MessageFeedback } from "@/components/molecules/multi-agent/MessageFeedback";
import { Button } from "@/components/atoms/multi-agent/Button";
import type { MultiAgentState } from "@/stores/multiAgentStore";

type ArtifactRow = MultiAgentState["artifacts"][number];

/** Compact Export dropdown — .md / PDF / Word.
 *  Opens below the trigger; closes on Escape or click-outside. */
function ExportMenu({ onExport, busy = false }: { onExport: (ext: "md" | "pdf" | "docx") => void; busy?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onPointer = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  const items: { label: string; ext: "md" | "pdf" | "docx"; icon: React.ReactNode }[] = [
    { label: "Markdown (.md)", ext: "md",   icon: <FileText className="h-3.5 w-3.5" /> },
    { label: "PDF",            ext: "pdf",  icon: <FileType className="h-3.5 w-3.5" /> },
    { label: "Word (.docx)",   ext: "docx", icon: <Download  className="h-3.5 w-3.5" /> },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <Button
        variant="ghost"
        size="sm"
        // Assembling the cited-evidence appendix fetches the cited artifacts, so an
        // export is no longer instant — the trigger reports that rather than looking
        // dead while the file is built.
        busy={busy}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ borderRadius: 999 }}
      >
        <span className="flex items-center gap-1">
          Export
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </Button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 160,
            padding: "4px",
            borderRadius: "var(--ma-radius-sm)",
            background: "var(--ma-surface)",
            border: "1px solid var(--ma-border)",
            boxShadow: "var(--ma-shadow-md)",
            zIndex: 200,
          }}
        >
          {items.map(({ label, ext, icon }) => (
            <button
              key={ext}
              role="menuitem"
              type="button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "6px 10px",
                borderRadius: "calc(var(--ma-radius-sm) - 2px)",
                fontSize: 12,
                color: "var(--ma-text)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
              }}
              onPointerEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--ma-surface-soft)"; }}
              onPointerLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
              onClick={() => {
                onExport(ext);
                setOpen(false);
              }}
            >
              <span style={{ color: "var(--ma-text-muted)", flex: "none" }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** True when `s` begins with the word "export" followed by whitespace. */
function startsWithExportWord(s: string): boolean {
  return s.length > 6 && s.slice(0, 6).toLowerCase() === "export" && isWhitespace(s[6]);
}

/** Allow only safe link schemes for model-generated markdown — blocks
 *  javascript:/data: (XSS). */
function isSafeHrefScheme(href: string): boolean {
  if (href.startsWith("#")) return true;
  const low = href.toLowerCase();
  return (
    low.startsWith("http:") ||
    low.startsWith("https:") ||
    low.startsWith("mailto:") ||
    low.startsWith("artifact:")
  );
}

export function buildArtifactInterceptor(artifacts: ArtifactRow[]) {
  const order = new Map<string, number>();
  let next = 0;
  const find = (id: string) =>
    artifacts.find((a) => a.id === id) ?? artifacts.find((a) => a.id.startsWith(id)) ?? null;

  const numberFor = (artifactId: string): number => {
    if (!order.has(artifactId)) {
      next += 1;
      order.set(artifactId, next);
    }
    return order.get(artifactId)!;
  };

  return {
    interceptor: {
      a: ({ href, children, ...rest }: { href?: string; children?: React.ReactNode } & Record<string, unknown>) => {
        const artifactHref =
          typeof href === "string" && href.startsWith("#artifact://") ? href.slice(1) : href;
        if (typeof artifactHref === "string" && artifactHref.startsWith("artifact://")) {
          const raw = artifactHref.slice("artifact://".length);
          const [id, qs] = raw.split("?");
          const match = find(id);
          if (match) {
            // "Export …" report artifacts (Markdown/PDF/DOCX) become real
            // download buttons instead of citation chips — the report content
            // lives in the artifact and is rendered to a file on click.
            const label = String(children ?? "");
            const isExport =
              startsWithExportWord(match.name || "") || startsWithExportWord(label);
            if (isExport) {
              return <ExportDownloadButton id={match.id} name={match.name || label || "Export"} />;
            }
            const expand = qs?.includes("expand=true");
            return (
              <CitationChip
                index={numberFor(match.id)}
                artifact={match}
                expand={expand}
              />
            );
          }
          return (
            <span
              className="ma-micro"
              style={{ color: "var(--ma-state-warn)", textTransform: "none", letterSpacing: 0 }}
              title={`Artifact not found: ${id}`}
            >
              [{id.slice(0, 8)}?]
            </span>
          );
        }
        // Only allow safe schemes — markdown is model-generated, so block
        // javascript:/data: hrefs (XSS) by dropping the href when unrecognized.
        const safeHref =
          typeof href === "string" && isSafeHrefScheme(href) ? href : undefined;
        const external = typeof safeHref === "string" && safeHref.startsWith("http");
        // Render real links in blue + underlined so the user knows they're
        // clickable (they used to render as plain text). External links open in
        // a new tab. Applies everywhere this interceptor is used (answer, past
        // turns, docs).
        return (
          <a
            href={safeHref}
            {...rest}
            className="ma-link"
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
          >
            {children}
          </a>
        );
      },
    },
    cited: () =>
      Array.from(order.entries())
        .map(([id, idx]) => ({ id, idx, artifact: artifacts.find((a) => a.id === id) ?? null }))
        .filter((e) => e.artifact),
  };
}

/** Real download button for an "Export …" report artifact (md / pdf / docx). */
function ExportDownloadButton({ id, name }: { id: string; name: string }) {
  const [busy, setBusy] = useState(false);
  // "Export PDF: [Refactor target: Foo.cls …]" -> "PDF"
  const fmt = (() => {
    let s = name;
    if (startsWithExportWord(s)) {
      s = s.slice(6);
      while (s.length && isWhitespace(s[0])) s = s.slice(1);
    }
    const colon = s.indexOf(":");
    if (colon >= 0) s = s.slice(0, colon);
    return s.trim() || "Download";
  })();
  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { downloadExportArtifact } = await import("@/services/multi-agent/exportArtifact");
      await downloadExportArtifact(id, name);
    } catch (e) {
      toast.error(`Download failed: ${(e as Error)?.message ?? String(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      className="ma-interactive inline-flex items-center gap-1.5 align-baseline"
      style={{
        padding: "3px 10px",
        fontSize: 11,
        borderRadius: 999,
        background: "var(--ma-accent-soft)",
        color: "var(--ma-accent)",
        fontWeight: 500,
      }}
      title={`Download ${fmt}`}
    >
      <Download className="h-3 w-3" />
      {busy ? "Preparing…" : `Download ${fmt}`}
    </button>
  );
}

interface ResultColumnProps {
  embedded?: boolean;
}

/** Blocked-state retrigger button (dev-agent parity) — "Start indexing" /
 *  "Retry indexing". POSTs the action endpoint; toasts success/error. */
function RetriggerIndexButton({ action }: { action: NonNullable<MultiAgentState["blockedAction"]> }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      await triggerRagIngest(action.endpoint);
      toast.success("Indexing started");
    } catch (e) {
      toast.error((e as Error).message || "Couldn't start indexing");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="ma-fade-up mt-3 inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/15 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/25 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy && <Loader2 className="h-3 w-3 animate-spin" />}
      <span>{action.label}</span>
    </button>
  );
}

export function ResultColumn({ embedded = false }: ResultColumnProps = {}) {
  const { finalMarkdown, textBuffer, artifacts: allArtifacts, status, runId, sessionId, thread, blockedAction } = useMultiAgentStore(
    useShallow((s) => ({
      finalMarkdown: s.finalMarkdown,
      textBuffer: s.textBuffer,
      artifacts: s.artifacts,
      status: s.status,
      runId: s.runId,
      sessionId: s.sessionId,
      thread: s.thread,
      blockedAction: s.blockedAction,
    }))
  );

  const streamingSynth = textBuffer["synthesizer"] || "";
  // This column renders the CURRENT run — show only ITS artifacts (tagged by
  // runId) so the answer's citations / diff / evidence aren't polluted by prior
  // runs' artifacts (the bug where everything piled up at the end of the chat).
  // Untagged rows (older/live edge) fall through to the current run.
  const artifacts = useMemo(
    () => allArtifacts.filter((a) => !a.runId || a.runId === runId),
    [allArtifacts, runId],
  );
  const isTerminal = status === "done" || status === "failed" || status === "cancelled";
  // Scrub mermaid at the SOURCE and MEMOIZE the result. Once a run is terminal,
  // ALWAYS render finalMarkdown (never fall back to the streaming buffer) so a
  // tab-focus re-render can't momentarily feed mermaid a truncated/streaming
  // diagram — the "tab away → Mermaid Parse error, hard-reload fine" bug. The
  // memo keys on the inputs, so identical content yields a stable string → the
  // memoized markdown component doesn't re-render → mermaid isn't re-parsed.
  const md = useMemo(() => {
    const final = sanitizeArtifactMarkdown(finalMarkdown).trim();
    const source = final || (isTerminal ? "" : sanitizeArtifactMarkdown(streamingSynth));
    return fixMermaidUnquotedLabels(source || final);
  }, [finalMarkdown, streamingSynth, isTerminal]);
  const isStreaming = !isTerminal && !finalMarkdown.trim() && streamingSynth.length > 0;
  const isPreSynth = !md && !isTerminal && (status === "running" || status === "planning" || status === "starting");
  const isFreshEmptySession = status === "idle" && !md && !runId && !sessionId && thread.length === 0;
  const isIdleContinuation = status === "idle" && !md && !isFreshEmptySession;

  const { interceptor, cited } = useMemo(() => buildArtifactInterceptor(artifacts), [artifacts]);

  // ── Entity recognition: make the run's KNOWN class names clickable in prose ──
  // Names the run actually touched (dependencies + impact callers/callees +
  // artifacts), mapped to their metadata id where we have one. Only these are
  // linkified — so no false positives on ordinary capitalised words.
  const dependencies = useMultiAgentIdeStore((s) => s.dependencies);
  const impactAnalysis = useMultiAgentIdeStore((s) => s.impactAnalysis);
  const setWorkspaceCodeRequest = useMultiAgentIdeStore((s) => s.setWorkspaceCodeRequest);
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);
  const knownEntities = useMemo(() => {
    const m = new Map<string, string | null>();
    const add = (name?: string | null, id?: string | null) => {
      const n = (name || "").trim();
      if (n.length < 3) return; // skip tiny tokens
      if (!m.has(n)) m.set(n, id ?? null);
      else if (id && !m.get(n)) m.set(n, id);
    };
    for (const d of dependencies) {
      add(d.target, d.target_id);
      for (const c of d.callers) add(c.name, c.id);
      for (const t of d.tests) add(t.name, t.id);
    }
    for (const e of impactAnalysis) {
      add(e.class_name);
      for (const c of e.callers || []) add(c.name);
      for (const c of e.callees || []) add(c.name);
    }
    return m;
  }, [dependencies, impactAnalysis]);

  const openClassByName = useCallback(
    async (name: string, id: string | null) => {
      try {
        const { getMetadataItem, getMetadataItemByName } = await import(
          "@/pages/multi-agent/ide/panels/resolveDependencyItemForEditor"
        );
        const resolvedId = id || (await getMetadataItemByName(name, orgId, true)).id;
        const full = await getMetadataItem(resolvedId, orgId, true);
        setWorkspaceCodeRequest({
          id: full.id,
          name: full.name || full.full_name || name,
          metadataType: full.metadata_type || "ApexClass",
          content: full.content || "",
          badge: "R",
        });
      } catch (e) {
        toast.error(`Couldn't open ${name}: ${(e as Error)?.message ?? "not found"}`);
      }
    },
    [orgId, setWorkspaceCodeRequest],
  );

  // Merge the artifact interceptor with prose renderers that linkify known
  // class names in paragraph / list-item / table-cell text.
  const proseComponents = useMemo(() => {
    const link = (children: React.ReactNode) => linkifyEntities(children, knownEntities, openClassByName);
    return {
      ...interceptor,
      p: ({ children, ...rest }: { children?: React.ReactNode } & Record<string, unknown>) => <p {...rest}>{link(children)}</p>,
      li: ({ children, ...rest }: { children?: React.ReactNode } & Record<string, unknown>) => <li {...rest}>{link(children)}</li>,
      td: ({ children, ...rest }: { children?: React.ReactNode } & Record<string, unknown>) => <td {...rest}>{link(children)}</td>,
    };
  }, [interceptor, knownEntities, openClassByName]);

  const { copied, copy: copyText } = useCopyToClipboard();
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [showAllEvidence, setShowAllEvidence] = useState(false);

  // Collapse the evidence panel whenever runId changes (new run or navigation
  // to a different run). useLayoutEffect — not useEffect — is required here:
  // useEffect fires after the browser paints, so the previous run's open
  // <details> would be visible for one frame before closing (flicker).
  // useLayoutEffect fires synchronously after React commits but before paint,
  // so the panel is already closed when the first pixel is drawn.
  //
  // Safe across all lifecycle paths:
  //  - startRun(): runId changes → resets → correct
  //  - hydrateFromDetail(): runId changes → resets → correct (different run)
  //  - Same-run reconnect: hydrateRun() early-returns when runId matches, no
  //    store write → effect never fires → expansion state preserved
  //  - Fresh mount: evidenceOpen is already false (useState(false)) → no-op
  useLayoutEffect(() => {
    setEvidenceOpen(false);
    setShowAllEvidence(false);
  }, [runId]);

  // Copy/download must NOT leak the internal artifact:// (or #artifact://)
  // citation scheme — strip those links to plain labels for export only. The
  // rendered `md` above keeps them so chips/inline editors still resolve.
  const exportMd = useMemo(() => exportArtifactMarkdown(md), [md]);
  const copy = () => {
    if (exportMd) void copyText(exportMd);
  };
  // Shared export: for PDF/Word, rasterize the architecture diagrams already
  // rendered in the answer (mermaid SVGs) and embed them as images; .md keeps the
  // source. Title comes from the user's question.
  const reportIntent = useUserIntent();
  const [exporting, setExporting] = useState(false);
  const runExport = async (ext: "md" | "pdf" | "docx") => {
    if (!exportMd || exporting) return;
    // Open the print window SYNCHRONOUSLY (before any await) so the click's
    // user-activation is still live — otherwise the browser backgrounds the
    // popup and window.print() renders only the first page.
    const printWin = ext === "pdf" ? window.open("", "_blank") : null;
    // Same reason as ReportExportBar: the appendix fetch happens before the real
    // document is written, and a blank popup in the meantime reads as a failure.
    writePrintPlaceholder(printWin, reportIntent || "Multi-agent report");
    setExporting(true);
    let diagrams: ReportDiagram[] = [];
    if (ext !== "md") {
      const root = document.querySelector("[data-ma-report-root]") as HTMLElement | null;
      try { diagrams = await collectRenderedDiagrams(root); } catch { diagrams = []; }
    }
    const title = (reportIntent || "Multi-agent report").slice(0, 90);
    try {
      // Built from the RAW `md`, not `exportMd`: the citations have to survive long
      // enough to be resolved into the appendix. buildExportMarkdown strips them
      // from the body exactly as exportArtifactMarkdown did.
      const body = await buildExportMarkdown(md);
      await downloadMarkdownAs(body, ext, "multi-agent-report", { diagrams, title, printWin });
    } catch (e) {
      try { printWin?.close(); } catch { /* ignore */ }
      toast.error(`Export failed: ${(e as Error)?.message ?? String(e)}`);
    } finally {
      setExporting(false);
    }
  };
  const citedIds = useMemo(() => new Set(cited().map((c) => c.id)), [cited]);
  const primaryDataArtifacts = useMemo(
    () => artifacts.filter((a) => ["soql_result", "tooling_query_result", "tooling_result", "data_result", "query_result"].includes((a.type || "").toLowerCase())),
    [artifacts],
  );
  const uncited = useMemo(
    () => artifacts.filter((a) => !citedIds.has(a.id) && !primaryDataArtifacts.some((d) => d.id === a.id)),
    [artifacts, citedIds, primaryDataArtifacts],
  );

  return (
    <div className={
      embedded
        ? (isFreshEmptySession
            ? "relative flex h-full min-h-0 w-full flex-1 flex-col overflow-visible"
            : "relative flex flex-col overflow-visible")
        : "relative flex h-full flex-col overflow-hidden"
    }>
      {/* Action toolbar — sticky row that spans full width (required for
          sticky to work) but is pointer-events-none so it never blocks
          clicks on the prose below. The frosted card inside is right-
          aligned and pointer-events-auto so it's still interactive. */}
      {md && (
        <div
          style={{
            position: "sticky",
            top: 12,
            zIndex: 40,
            display: "flex",
            justifyContent: "flex-end",
            paddingRight: 16,
            pointerEvents: "none",
          }}
        >
          <div
            className="flex items-center gap-2"
            style={{ pointerEvents: "auto" }}
          >
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Copy className="h-3.5 w-3.5" />}
              onClick={copy}
              style={{
                borderRadius: 999,
                border: "1px solid var(--ma-border)",
                background: "color-mix(in oklab, var(--ma-surface) 90%, transparent)",
                backdropFilter: "blur(10px)",
                boxShadow: "var(--ma-shadow-sm)",
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <div
              style={{
                borderRadius: 999,
                border: "1px solid var(--ma-border)",
                background: "color-mix(in oklab, var(--ma-surface) 90%, transparent)",
                backdropFilter: "blur(10px)",
                boxShadow: "var(--ma-shadow-sm)",
              }}
            >
              <ExportMenu onExport={runExport} busy={exporting} />
            </div>
          </div>
        </div>
      )}

      {/* Safety banner — when the backend's zero-evidence guard flagged
          the run, this strip warns the user that the answer below is
          NOT grounded in real org data. Highest-signal addition for
          audit-class lanes; pairs with the iter 7 backend fix. */}
      <NoEvidenceBanner />
      <ContractViolationBanner />
      <AutoFixFailedBanner />

      {/* Narration — natural-language LLM reasoning in the chat flow,
          above the answer. Reads the live extended-thinking trace so
          you see "Let me look at the source first…" as it happens.
          Shown for the WHOLE live run (not just pre-answer) so post-answer
          steps like auto_fix ("Building the apply-ready patch…") narrate
          instead of looking stuck. Narration self-hides when there's no
          active reasoning, and disappears once the run is terminal. */}
      {!isTerminal && <Narration />}

      {/* Idle hero — rendered OUTSIDE ma-prose in a flex-1 centred container so
          it sits in the true middle of the chat area (ma-prose's fixed padding
          + max-width otherwise pin it to the top-left). */}
      {isFreshEmptySession ? (
        <div className={embedded ? "relative flex h-full min-h-0 w-full flex-1 flex-col" : "relative flex-1 overflow-auto"}>
          <div className="flex h-full min-h-[560px] flex-1 items-center justify-center px-6 py-8">
            <IdleHero />
          </div>
        </div>
      ) : (
      <div className={embedded ? "relative" : "relative flex-1 overflow-auto"}>
        <div aria-live="polite" aria-atomic="false">
        <article className="ma-prose ma-prose--active-chat" data-ma-report-root>
          {isIdleContinuation ? (
            null
          ) : isPreSynth && !md ? (
            <PreSynthSkeleton />
          ) : status === "cancelled" && !md ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center text-muted-foreground">
              <div className="text-[15px] font-medium text-foreground">You stopped this run</div>
              <div className="text-[11px] font-medium uppercase tracking-[0.08em]">Ask again or refine your question to continue</div>
            </div>
          ) : !md ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center text-muted-foreground">
              <div className="text-[15px] font-medium text-foreground">No result produced</div>
              <div className="text-[11px] font-medium uppercase tracking-[0.08em]">Try rephrasing the question</div>
            </div>
          ) : (
            <SkillResultMarkdown
              content={md}
              isStreaming={isStreaming}
              components={proseComponents as unknown as Record<string, unknown>}
            />
          )}

          {isStreaming && <StreamingCursor />}

          {/* Answer is on screen but the run is STILL going (auto_fix / verifier /
              more to follow) — once streaming ends the answer looks done, so an
              animated shimmer + pulse under it catches the eye that more may come.
              Disappears the instant the run goes terminal (then chips/feedback show). */}
          {md && !isTerminal && !isStreaming && <StillWorking />}

          {/* Blocked-state retrigger (dev-agent parity) — when the org's code
              index is not_started/failed, the run is blocked with a message and
              this button kicks off ingest. in_progress/metadata = no action. */}
          {isTerminal && blockedAction?.endpoint && blockedAction.enabled !== false && (
            <RetriggerIndexButton action={blockedAction} />
          )}

          {/* Proposed code changes — shown below the prose so the answer
              context reads first, then the diff confirms what changed. */}
          {isTerminal && md && <AutoFixStrip />}

          {/* Download this report — real, working client-side export (generates
              the file from the rendered report; no backend artifact needed).
              Extracted into <ReportExportBar> so PAST turns (rendered in
              ChatPanel, which owns no export UI) get the same working strip.
              Passes the RAW markdown (not `exportMd`): the bar strips the
              `artifact://` citations itself for the body, and needs them intact to
              resolve each cited artifact into the exported "Appendix — cited
              evidence". Passing the pre-stripped text is what would silently ship a
              report whose appendix is empty. */}
          {isTerminal && md && (
            <ReportExportBar markdown={md} title={reportIntent || "Multi-agent report"} />
          )}

          {!isPreSynth && md && cited().length > 0 && <SourcesStrip cited={cited()} />}

          {isTerminal && primaryDataArtifacts.length > 0 && (
            <div className="mt-6 flex flex-col gap-3">
              <div className="ma-micro" style={{ color: "var(--ma-text-muted)" }}>
                Data results
              </div>
              {primaryDataArtifacts.map((a) => (
                <ArtifactCard key={a.id} artifact={a} />
              ))}
            </div>
          )}

          {/* Verifier gaps — what's missing or unverified, surfaced once
              the run is terminal so the user can ask a sharper follow-up. */}
          {isTerminal && md && <VerifierGaps />}

          {/* Follow-up chips — lane-aware suggested next questions
              rendered as clickable cards. Click → fires /refine with
              the suggested intent. Only after the run terminates. */}
          {isTerminal && md && <FollowUpChips />}

          {/* Quick feedback at the very end of the answer.
              ThumbsUp = simple positive rating.
              ThumbsDown = opens BadResponsePopover with reason picker. */}
          {isTerminal && md && <MessageFeedback runId={runId} />}

          {uncited.length > 0 && (
            <details
              className="ma-evidence-panel mt-8 overflow-hidden"
              style={{
                borderRadius: "var(--ma-radius-md)",
                border: "1px solid var(--ma-border)",
                background: "var(--ma-surface-soft)",
              }}
              open={evidenceOpen}
              onToggle={(e) => setEvidenceOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary
                className="flex cursor-pointer items-center gap-2 px-3 py-2.5 select-none transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
                style={{ listStyle: "none" }}
              >
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ma-text)" }}>
                  Supporting evidence
                </span>
                <span
                  className="ma-micro"
                  style={{
                    padding: "1px 7px",
                    borderRadius: 999,
                    background: "var(--ma-surface)",
                    border: "1px solid var(--ma-border)",
                    textTransform: "none",
                    letterSpacing: 0,
                    color: "var(--ma-text-muted)",
                  }}
                >
                  {uncited.length}
                </span>
                <span
                  className="ml-auto ma-micro"
                  style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}
                >
                  {evidenceOpen ? "Hide" : "Show"}
                </span>
              </summary>
              <div
                className="flex flex-col gap-2 px-3 py-3"
                style={{ borderTop: "1px solid var(--ma-border)" }}
              >
                {(showAllEvidence ? uncited : uncited.slice(-30)).map((a) => (
                  <ArtifactCard key={a.id} artifact={a} />
                ))}
                {uncited.length > 30 && (
                  <button
                    type="button"
                    onClick={() => setShowAllEvidence((v) => !v)}
                    className="ma-micro mt-1 text-left"
                    style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-accent)", cursor: "pointer" }}
                  >
                    {showAllEvidence
                      ? "Show less"
                      : `Showing 30 of ${uncited.length} — Show all`}
                  </button>
                )}
              </div>
            </details>
          )}
        </article>
        </div>
      </div>
      )}
    </div>
  );
}


function VerifierGaps() {
  const verifier = useMultiAgentStore((s) => s.verifier);
  if (!verifier || verifier.gaps.length === 0) return null;
  return (
    <div
      className="ma-fade-up mt-8"
      style={{
        background: "var(--ma-surface-soft)",
        borderRadius: "var(--ma-radius-md)",
        padding: "12px 16px",
      }}
    >
      <div className="ma-micro mb-2 flex items-center gap-2" style={{ color: "var(--ma-text-muted)" }}>
        Gaps · {verifier.gaps.length}
        <span
          className="ml-auto"
          style={{
            fontSize: 11,
            color: "var(--ma-text-muted)",
            textTransform: "none",
            letterSpacing: 0,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          Quality {verifier.score}/10
        </span>
      </div>
      <ul className="m-0 flex flex-col gap-1 p-0" style={{ listStyle: "none" }}>
        {verifier.gaps.map((g, i) => (
          <li key={i} style={{ fontSize: 12, color: "var(--ma-text-muted)", lineHeight: 1.5 }}>
            · {g}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Normalize an artifact name to a per-target dedupe key: drop a leading
// "auto_fix:", remove all "refactor"/".cls"/".trigger" occurrences, keep only
// [a-z0-9], cap at 48 chars.
function normalizeAutoFixKey(name: string | null | undefined): string {
  let s = (name || "").toLowerCase();
  if (s.startsWith("auto_fix:")) s = s.slice("auto_fix:".length);
  s = s.split("refactor").join("");
  s = s.split(".cls").join("").split(".trigger").join("");
  let out = "";
  for (const ch of s) {
    if ((ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9")) out += ch;
  }
  return out.slice(0, 48);
}

function AutoFixStrip() {
  const fixes = useMultiAgentStore((s) => s.proposedFixes);
  const allArtifacts = useMultiAgentStore((s) => s.artifacts);
  const runId = useMultiAgentStore((s) => s.runId);
  // Only the CURRENT run's diffs — prior runs' patches render under their OWN
  // turn (ChatPanel), not piled onto the latest answer at the bottom.
  const artifacts = useMemo(
    () => allArtifacts.filter((a) => !a.runId || a.runId === runId),
    [allArtifacts, runId],
  );
  // Code/diff artifacts carry the FULL patch (original + replacement + diff).
  // auto_fix runs after synthesis so it's never cited inline — surface the
  // complete change here.
  // Dedupe to the LATEST patch per target. Artifacts persist across runs now, so
  // an earlier (pre-fix, empty-original) diff for a class would otherwise render
  // as a broken single-editor next to the newer complete-pair diff. Keep the most
  // recent per normalized name (different classes keep their own diff). Memoized
  // so this isn't rebuilt on every parent render during streaming.
  const patchArtifacts = useMemo(() => {
    const latest = new Map<string, ArtifactRow>();
    for (const a of artifacts) {
      const t = (a.type || "").toLowerCase();
      if (t !== "diff" && t !== "code_patch") continue;
      const k = normalizeAutoFixKey(a.name) || a.id;
      const prev = latest.get(k);
      if (!prev || (a.createdAt || 0) >= (prev.createdAt || 0)) latest.set(k, a);
    }
    return Array.from(latest.values()).sort(
      (x, y) => (y.createdAt || 0) - (x.createdAt || 0),
    );
  }, [artifacts]);
  if (fixes.length === 0 && patchArtifacts.length === 0) return null;
  const riskColor = (r: string): string =>
    r === "high" ? "var(--ma-state-err)" :
    r === "medium" ? "var(--ma-state-warn)" :
    "var(--ma-state-ok)";
  // MANDATORY: for a refactor, always show the diff (current vs new source in
  // colour) AND every new class — via ArtifactCard -> CodePatchView, which
  // renders a Monaco DiffEditor + current/new panels per fix (so 7 new classes
  // show as 7 diffs). Fall back to summary chips only when there's no patch
  // artifact (e.g. auto_fix produced no complete-code change).
  if (patchArtifacts.length > 0) {
    return (
      <div className="mt-10">
        <div className="ma-micro mb-3" style={{ color: "var(--ma-text-muted)" }}>
          Proposed changes · {patchArtifacts.length}
        </div>
        <div className="flex flex-col gap-3">
          {patchArtifacts.map((a) => <ArtifactCard key={a.id} artifact={a} />)}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-10">
      <div className="ma-micro mb-3" style={{ color: "var(--ma-text-muted)" }}>
        Proposed fixes · {fixes.length}
      </div>
      <div className="flex flex-col gap-2">
        {fixes.map((f, i) => (
          <div
            key={i}
            className="ma-interactive ma-fade-up flex items-start gap-3 px-3 py-2.5"
            style={{
              background: "var(--ma-grad-surface)",
              borderRadius: "var(--ma-radius-md)",
              boxShadow: "var(--ma-shadow-sm)",
            }}
          >
            <span
              style={{
                marginTop: 4,
                width: 8, height: 8, borderRadius: 999,
                background: riskColor(f.risk),
                flex: "none",
              }}
            />
            <div className="min-w-0 flex-1">
              <div style={{ fontSize: 13, color: "var(--ma-text)", fontWeight: 500 }}>
                {f.summary || f.file || f.component_name || f.target}
              </div>
              <div className="ma-micro" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}>
                {f.kind}
                {(f.file || f.target) && ` · ${f.file || f.target}`}
                {f.metadata_type && ` · ${f.metadata_type}`}
                {f.validation_status && ` · ${humanizeStatus(f.validation_status)}`}
              </div>
            </div>
            <span
              className="ma-micro"
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                background: `color-mix(in oklab, ${riskColor(f.risk)} 14%, transparent)`,
                color: riskColor(f.risk),
                textTransform: "uppercase",
                fontWeight: 600,
                flex: "none",
              }}
            >
              {f.risk}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}




function StreamingCursor() {
  return (
    <span
      aria-hidden
      className="inline-block align-baseline"
      style={{
        width: 2,
        height: 14,
        marginLeft: 2,
        background: "var(--ma-accent)",
        animation: "ma-pulse 1s steps(2) infinite",
        verticalAlign: "text-bottom",
      }}
    />
  );
}

function PreSynthSkeleton() {
  return (
    <div className="flex items-start gap-3">
      {/* Tiny pulse strip — sits next to the shimmer rows, not the whole page */}
      <span
        aria-hidden
        style={{
          width: 2,
          alignSelf: "stretch",
          minHeight: 64,
          borderRadius: 2,
          background: "var(--ma-accent)",
          animation: "ma-pulse 1.6s var(--ma-ease) infinite",
          flex: "none",
        }}
      />
      <div className="flex flex-1 flex-col gap-3">
        {/* Weave warm-up line blended in as the top shimmer row (shimmer-text);
            self-hides the moment narration flows, leaving just the skeleton. */}
        <WeaveShimmerCaption />
        <div className="ma-skeleton h-3 w-5/6" />
        <div className="ma-skeleton h-3 w-1/2" />
      </div>
    </div>
  );
}

// Eye-catching "still working" strip shown UNDER the answer while the run keeps
// going after the text landed. The motion (pulsing dot + shimmer sweep) is the
// signal; the current-step label is secondary. Reuses existing ma-dot--busy /
// ma-skeleton animations — no new keyframes.
function StillWorking() {
  const nodes = useMultiAgentStore((s) => s.nodes);
  const active = Object.values(nodes).find(
    (n) => n.status === "running" || n.status === "tool_calling",
  );
  const label = (active?.task || "Still working").trim();
  return (
    <div className="ma-fade-up mt-4 flex items-center gap-2.5" aria-live="polite">
      <span className="ma-dot ma-dot--busy" style={{ width: 7, height: 7, flex: "none" }} />
      <div className="ma-skeleton" style={{ height: 7, flex: 1, maxWidth: 200 }} />
      <span className="ma-micro" style={{ color: "var(--ma-text-muted)", whiteSpace: "nowrap" }}>
        {label.length > 48 ? `${label.slice(0, 48)}…` : `${label}…`}
      </span>
    </div>
  );
}

const SAMPLE_PROMPTS: { title: string; lane: string }[] = [
  { title: "What are the most complex classes in this org?",            lane: "Quick" },
  { title: "Which automation runs on every Lead creation?",             lane: "Quick" },
  { title: "Audit risk in this org's data model",                       lane: "Risk audit" },
  { title: "Find untested Apex with high call volume",                  lane: "Apex audit" },
];

function IdleHero() {
  const fillPrompt = (text: string) => {
    const ta = document.querySelector<HTMLTextAreaElement>(".ma-route textarea");
    if (ta) {
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value",
      )?.set;
      setter?.call(ta, text);
      ta.dispatchEvent(new Event("input", { bubbles: true }));
      ta.focus();
    }
  };

  // Token orbit positions (precomputed translate so labels can counter-rotate
  // to stay upright while the ring revolves). r = 70px.
  const ORBIT = [
    { x: 70, y: 0, label: "A", color: "hsl(212 84% 52%)", title: "Account" },
    { x: 0, y: 70, label: "C", color: "hsl(150 64% 40%)", title: "Contact" },
    { x: -70, y: 0, label: "O", color: "hsl(28 92% 50%)", title: "Opportunity" },
    { x: 0, y: -70, label: "Cs", color: "hsl(0 72% 51%)", title: "Case" },
  ];

  return (
    <div className="flex w-full max-w-[600px] flex-col items-center justify-center gap-10 text-center">
      {/* Salesforce-flavored centerpiece — a cloud at the hub with four SObject
          tokens that genuinely ORBIT it (the ring rotates; each label
          counter-rotates to stay upright), over a soft pulsing glow. */}
      <div className="relative" style={{ width: 180, height: 180 }}>
        {/* Soft radial glow */}
        <span
          aria-hidden
          className="absolute inset-0"
          style={{
            borderRadius: 999,
            background:
              "radial-gradient(circle at center, color-mix(in oklab, var(--ma-accent) 38%, transparent), transparent 68%)",
            filter: "blur(6px)",
            animation: "ma-orbit-glow 3.2s ease-in-out infinite",
          }}
        />
        {/* Faint dashed orbit path */}
        <span
          aria-hidden
          className="absolute"
          style={{
            inset: 19,
            borderRadius: 999,
            border: "1px dashed color-mix(in oklab, var(--ma-accent) 30%, transparent)",
          }}
        />
        {/* Hub cloud */}
        <span
          aria-hidden
          className="absolute inset-0 m-auto inline-flex items-center justify-center"
          style={{
            width: 64,
            height: 64,
            borderRadius: 999,
            background: "var(--ma-grad-accent)",
            color: "var(--ma-accent)",
            boxShadow: "var(--ma-shadow-md)",
            animation: "ma-pulse 2.8s ease-in-out infinite",
          }}
        >
          <Cloud className="h-7 w-7" />
        </span>
        {/* Rotating ring */}
        <div
          aria-hidden
          className="ma-orbit-ring absolute inset-0"
          style={{ animation: "ma-orbit 22s linear infinite" }}
        >
          {ORBIT.map(({ x, y, label, color, title }) => (
            <span
              key={title}
              title={title}
              className="absolute"
              style={{
                top: "50%",
                left: "50%",
                width: 26,
                height: 26,
                marginTop: -13,
                marginLeft: -13,
                transform: `translate(${x}px, ${y}px)`,
              }}
            >
              {/* Counter-rotate so the glyph stays upright as the ring turns. */}
              <span
                className="ma-orbit-chip inline-flex h-full w-full items-center justify-center"
                style={{
                  borderRadius: 7,
                  background: color,
                  color: "white",
                  fontSize: 11,
                  fontWeight: 700,
                  boxShadow: "var(--ma-shadow-sm)",
                  animation: "ma-orbit-rev 22s linear infinite",
                }}
              >
                {label}
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className="flex w-full flex-col items-center gap-3">
        <h1 className="ma-hero" style={{ color: "var(--ma-text)", fontSize: "clamp(22px, 4vw, 32px)" }}>
          What should the agents do?
        </h1>
        <p className="text-[15px] leading-[1.65]" style={{ color: "var(--ma-text-muted)", maxWidth: "min(480px, 100%)" }}>
          Quick questions get one agent. Complex audits and changes get a small
          team that fans out, cites artifacts, and proposes fixes.
        </p>
      </div>

      <div
        className="w-full"
        style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", maxWidth: 560 }}
      >
        {SAMPLE_PROMPTS.map((p) => (
          <button
            key={p.title}
            type="button"
            onClick={() => fillPrompt(p.title)}
            className="ma-interactive ma-tile flex min-w-0 flex-col items-start gap-1 text-left"
            style={{ padding: "12px 14px" }}
          >
            <span className="w-full break-words" style={{ fontSize: 13, fontWeight: 500, color: "var(--ma-text)" }}>
              {p.title}
            </span>
            <span
              className="ma-micro w-full break-words"
              style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}
            >
              {p.lane}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SourcesStrip({
  cited,
}: {
  cited: { id: string; idx: number; artifact: ArtifactRow | null }[];
}) {
  // Open the artifact in the layover on click — these were raw
  // <a href="artifact://…"> anchors, so the browser tried to "launch" the
  // artifact:// scheme (no handler → console error) and nothing opened.
  const openOverlay = useArtifactOverlayStore((s) => s.open);
  return (
    <div
      className="ma-fade-up mt-10 flex flex-wrap items-center gap-2 pt-5"
      style={{ borderTop: "1px solid var(--ma-border)" }}
    >
      <span className="ma-micro" style={{ color: "var(--ma-text-muted)" }}>
        Sources
      </span>
      {cited.map(({ id, idx, artifact }) =>
        artifact ? (
          <button
            key={id}
            type="button"
            onClick={() => openOverlay(artifact)}
            className="ma-interactive inline-flex items-center gap-1.5"
            style={{
              padding: "4px 10px",
              fontSize: 11,
              borderRadius: 999,
              background: "var(--ma-accent-soft)",
              color: "var(--ma-accent)",
              fontWeight: 500,
            }}
            title={sanitizeArtifactLabel(artifact.name, artifact.id.slice(0, 8))}
          >
            <span style={{ fontVariantNumeric: "tabular-nums" }}>[{idx}]</span>
            <span className="max-w-[180px] truncate" style={{ fontWeight: 400, color: "var(--ma-text)" }}>
              {sanitizeArtifactLabel(artifact.name, artifact.id.slice(0, 8))}
            </span>
          </button>
        ) : null,
      )}
    </div>
  );
}
